import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormGroup,
  FormControlLabel,
  Typography,
} from "@mui/material";
import apiClient from "../utils/apiClient";

// Available plot form fields that can be configured
const AVAILABLE_PLOT_FIELDS = [
  { key: "soil_type", label: "Soil Type" },
  { key: "dimensions", label: "Width/Depth" },
  { key: "description", label: "Description" },
  { key: "shape", label: "Shape" },
];

const PlotTypeForm = ({ onSubmit, onCancel, initialData = null }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    default_shape: "rectangle",
    allowed_crop_type_uuids: [],
    form_fields: [],
  });

  const [cropTypes, setCropTypes] = useState([]);

  useEffect(() => {
    const fetchCropTypes = async () => {
      try {
        const response = await apiClient.get("/croptypes");
        setCropTypes(response.data);
      } catch (err) {
        console.error("Error fetching crop types:", err);
      }
    };
    fetchCropTypes();

    if (initialData) {
      setFormData({
        name: initialData.name || "",
        description: initialData.description || "",
        default_shape: initialData.default_shape || "rectangle",
        allowed_crop_type_uuids: initialData.allowed_crop_types?.map(ct => ct.crop_type_uuid) || [],
        form_fields: initialData.form_fields || [],
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCropTypesChange = (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      allowed_crop_type_uuids: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleFieldToggle = (fieldKey) => {
    setFormData((prev) => {
      const fields = prev.form_fields.includes(fieldKey)
        ? prev.form_fields.filter((f) => f !== fieldKey)
        : [...prev.form_fields, fieldKey];
      return { ...prev, form_fields: fields };
    });
  };

  const handleSubmit = async () => {
    const normalized = Object.fromEntries(
      Object.entries(formData).map(([k, v]) => [k, v === "" ? null : v])
    );
    onSubmit(normalized);
  };

  return (
    <Box>
      <TextField 
        fullWidth 
        label="Name" 
        name="name" 
        value={formData.name} 
        onChange={handleChange} 
        margin="normal" 
        size="small" 
        required
      />
      <TextField 
        fullWidth 
        label="Description" 
        name="description" 
        value={formData.description} 
        onChange={handleChange} 
        margin="normal" 
        multiline 
        rows={2} 
        size="small" 
      />
      <FormControl fullWidth margin="normal" size="small">
        <InputLabel id="default-shape-label">Default Shape</InputLabel>
        <Select
          labelId="default-shape-label"
          name="default_shape"
          value={formData.default_shape}
          onChange={handleChange}
        >
          <MenuItem value="rectangle">Rectangle</MenuItem>
          <MenuItem value="circle">Circle</MenuItem>
          <MenuItem value="triangle">Triangle</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth margin="normal" size="small">
        <InputLabel id="allowed-crop-types-label">Allowed Crop Types</InputLabel>
        <Select
          labelId="allowed-crop-types-label"
          multiple
          value={formData.allowed_crop_type_uuids}
          onChange={handleCropTypesChange}
          input={<OutlinedInput label="Allowed Crop Types" />}
          renderValue={(selected) =>
            selected.map((id) => cropTypes.find((ct) => ct.crop_type_uuid === id)?.name || id).join(", ")
          }
        >
          {cropTypes.map((cropType) => (
            <MenuItem key={cropType.crop_type_uuid} value={cropType.crop_type_uuid}>
              <Checkbox checked={formData.allowed_crop_type_uuids.includes(cropType.crop_type_uuid)} />
              <ListItemText primary={cropType.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ marginTop: 3 }}>
        <Typography variant="h6" gutterBottom>Form Fields</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
          Select which fields should be shown when creating/editing plots of this type
        </Typography>
        <FormGroup>
          {AVAILABLE_PLOT_FIELDS.map((field) => (
            <FormControlLabel
              key={field.key}
              control={
                <Checkbox
                  checked={formData.form_fields.includes(field.key)}
                  onChange={() => handleFieldToggle(field.key)}
                />
              }
              label={field.label}
            />
          ))}
        </FormGroup>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
        <Button onClick={onCancel} className="vintage-button">Cancel</Button>
        <Button variant="outlined" onClick={handleSubmit} className="vintage-button">
          {initialData ? "Update" : "Create"}
        </Button>
      </Box>
    </Box>
  );
};

export default PlotTypeForm;
